// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
import type {Ball} from './Ball'
import type {World} from './World'
import {applyContactImpulse,correctPenetration} from './contact'
import {PENETRATION_SLOP} from './constants'
import type {MaterialName} from './materials'

/** A convex manufactured plate, in the mechanism's along/inward frame. */
export interface PlateProfile {
  readonly referenceLength:number
  readonly outline:readonly (readonly [number,number])[]
  readonly bottom:number
  readonly top:number
  readonly material?:MaterialName
  readonly active?:boolean
}

/** Finite convex extrusion; no render mesh or triangle soup enters the simulation. */
export class Plate {
  private readonly xs:number[]=[]
  private readonly ys:number[]=[]
  private readonly lo:number
  private readonly hi:number
  private minX=Infinity;private maxX=-Infinity;private minY=Infinity;private maxY=-Infinity
  private nx=0;private ny=0;private nz=0;private depth=0
  readonly active:boolean
  private readonly material:MaterialName
  impactSpeed=0
  get normalX():number{return this.nx}
  get normalY():number{return this.ny}
  get normalZ():number{return this.nz}
  constructor(profile:PlateProfile,ax:number,ay:number,bx:number,by:number){
    const dx=bx-ax,dy=by-ay,length=Math.sqrt(dx*dx+dy*dy),scale=length/profile.referenceLength
    this.lo=profile.bottom;this.hi=profile.top
    this.active=profile.active??false;this.material=profile.material??'plastic'
    for(const p of profile.outline){
      const x=(ax+bx)/2+(dx*p[0]+dy*p[1])/profile.referenceLength,y=(ay+by)/2+(dy*p[0]-dx*p[1])/profile.referenceLength
      this.xs.push(x);this.ys.push(y);this.minX=Math.min(this.minX,x);this.maxX=Math.max(this.maxX,x);this.minY=Math.min(this.minY,y);this.maxY=Math.max(this.maxY,y)
    }
    if(!(scale>0)||this.xs.length<3)throw Error('Invalid plate profile')
    let area=0;for(let i=0;i<this.xs.length;i++){const j=(i+1)%this.xs.length;area+=this.xs[i]!*this.ys[j]!-this.ys[i]!*this.xs[j]!}
    if(area<0){this.xs.reverse();this.ys.reverse()}
  }
  private probe(x:number,y:number,z:number,r:number):void{
    let inside=true,nearest=Infinity,cx=x,cy=y,enx=1,eny=0
    for(let i=0;i<this.xs.length;i++){
      const j=(i+1)%this.xs.length,ax=this.xs[i]!,ay=this.ys[i]!,dx=this.xs[j]!-ax,dy=this.ys[j]!-ay,qx=x-ax,qy=y-ay
      if(dx*qy-dy*qx<0)inside=false
      const t=Math.max(0,Math.min(1,(qx*dx+qy*dy)/(dx*dx+dy*dy))),px=ax+t*dx,py=ay+t*dy,d2=(x-px)*(x-px)+(y-py)*(y-py)
      if(d2<nearest){nearest=d2;cx=px;cy=py;const length=Math.sqrt(dx*dx+dy*dy);enx=dy/length;eny=-dx/length}
    }
    if(inside&&z>=this.lo&&z<=this.hi){
      const edge=Math.sqrt(nearest),lower=z-this.lo,upper=this.hi-z
      if(lower<=upper&&lower<=edge){this.nx=0;this.ny=0;this.nz=-1;this.depth=r+lower}
      else if(upper<=edge){this.nx=0;this.ny=0;this.nz=1;this.depth=r+upper}
      else {this.nx=edge>0?(cx-x)/edge:enx;this.ny=edge>0?(cy-y)/edge:eny;this.nz=0;this.depth=r+edge}
      return
    }
    const vx=inside?0:x-cx,vy=inside?0:y-cy,vz=z-Math.max(this.lo,Math.min(this.hi,z)),distance=Math.sqrt(vx*vx+vy*vy+vz*vz)
    this.nx=distance>0?vx/distance:0;this.ny=distance>0?vy/distance:0;this.nz=distance>0?vz/distance:1;this.depth=r-distance
  }
  resolve(world:World,ball:Ball):boolean{
    if(ball.layer!==0)return false
    const x=ball.x-world.frameX,y=ball.y-world.frameY,z=ball.z,r=ball.radius,x0=ball.prevX-world.frameX,y0=ball.prevY-world.frameY,z0=ball.prevZ
    if(Math.max(x,x0)+r<this.minX||Math.min(x,x0)-r>this.maxX||Math.max(y,y0)+r<this.minY||Math.min(y,y0)-r>this.maxY||Math.max(z,z0)+r<this.lo||Math.min(z,z0)-r>this.hi)return false
    this.probe(x,y,z,r)
    if(this.depth<=PENETRATION_SLOP){
      const dx=x-x0,dy=y-y0,dz=z-z0,travel=Math.sqrt(dx*dx+dy*dy+dz*dz)
      if(travel<r*.5)return false
      this.probe(x0,y0,z0,r);if(this.depth>=-PENETRATION_SLOP)return false
      let t=0,found=false
      for(let i=0;i<64;i++){
        this.probe(x0+dx*t,y0+dy*t,z0+dz*t,r)
        if(this.depth>=-1e-8){found=true;break}
        t+=-this.depth/travel*.95;if(t>1)break
      }
      if(!found)return false
      ball.x=x0+dx*t+world.frameX;ball.y=y0+dy*t+world.frameY;ball.z=z0+dz*t
    }
    correctPenetration(ball,this.nx,this.ny,this.nz,Math.max(0,this.depth))
    this.impactSpeed=applyContactImpulse(ball,this.nx,this.ny,this.nz,world.materials[this.material],world.frameVX,world.frameVY,0,world.rng).impactSpeed
    return true
  }
}
